import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Plus, Edit, Trash2, Save, X, Search, Filter, Globe, AlertCircle, Check, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { admin } from '@/lib/api';
import { toast } from 'sonner';
import SellerTranslationStatusTab from './SellerTranslationStatusTab';

const SUPPORTED_LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
];

const CATEGORIES = [
  { key: 'countries', name: 'Ülke İsimleri', description: 'İletişim sayfasındaki ülke isimleri' },
  { key: 'nav', name: 'Navigasyon', description: 'Menü ve navigasyon metinleri' },
  { key: 'common', name: 'Genel', description: 'Genel kullanılan metinler' },
  { key: 'contact', name: 'İletişim', description: 'İletişim sayfası metinleri' },
  { key: 'footer', name: 'Alt Bilgi', description: 'Footer bölümü metinleri' },
  { key: 'product', name: 'Ürün', description: 'Ürün sayfası metinleri' },
  { key: 'cart', name: 'Sepet', description: 'Sepet ve ödeme metinleri' },
  { key: 'auth', name: 'Kimlik Doğrulama', description: 'Giriş ve kayıt metinleri' },
  { key: 'custom', name: 'Özel', description: 'Özel tanımlı metinler' },
];

const TranslationManagementTab = () => {
  const { t } = useTranslation();
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editDialog, setEditDialog] = useState({ open: false, translation: null, isNew: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, translation: null });
  const [activeLanguage, setActiveLanguage] = useState('tr');

  useEffect(() => {
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    try {
      setLoading(true);
      const response = await admin.getTranslations();
      setTranslations(response.data.translations || []);
    } catch (error) {
      console.error('Failed to fetch translations:', error);
      toast.error('Çeviriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredTranslations = useMemo(() => {
    return translations.filter(t => {
      const matchesSearch = searchQuery === '' || 
        t.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        Object.values(t.translations || {}).some(v => 
          v && v.toLowerCase().includes(searchQuery.toLowerCase())
        );
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [translations, searchQuery, categoryFilter]);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editDialog.isNew) {
        await admin.createTranslation(data);
        toast.success('Çeviri eklendi');
      } else {
        await admin.updateTranslation(editDialog.translation.id, data);
        toast.success('Çeviri güncellendi');
      }
      setEditDialog({ open: false, translation: null, isNew: false });
      fetchTranslations();
    } catch (error) {
      console.error('Failed to save translation:', error);
      toast.error(error.response?.data?.detail || 'Çeviri kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await admin.deleteTranslation(deleteDialog.translation.id);
      toast.success('Çeviri silindi');
      setDeleteDialog({ open: false, translation: null });
      fetchTranslations();
    } catch (error) {
      console.error('Failed to delete translation:', error);
      toast.error('Çeviri silinemedi');
    }
  };

  const openNewDialog = () => {
    setEditDialog({
      open: true,
      isNew: true,
      translation: {
        key: '',
        category: 'custom',
        translations: SUPPORTED_LANGUAGES.reduce((acc, lang) => {
          acc[lang.code] = '';
          return acc;
        }, {})
      }
    });
  };

  const openEditDialog = (translation) => {
    setEditDialog({
      open: true,
      isNew: false,
      translation: {
        ...translation,
        translations: {
          ...SUPPORTED_LANGUAGES.reduce((acc, lang) => {
            acc[lang.code] = '';
            return acc;
          }, {}),
          ...translation.translations
        }
      }
    });
  };

  const getTranslationCompleteness = (translation) => {
    const filled = Object.values(translation.translations || {}).filter(v => v && v.trim()).length;
    return Math.round((filled / SUPPORTED_LANGUAGES.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="translation-management-tab">
      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="manual" data-testid="tab-manual-translations">
            <Languages className="w-4 h-4 mr-1.5" /> Ceviri Yonetimi
          </TabsTrigger>
          <TabsTrigger value="seller-status" data-testid="tab-seller-translations">
            <Globe className="w-4 h-4 mr-1.5" /> Bayi Ceviri Durumu
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Languages className="w-5 h-5" />
                Çeviri Yönetimi
              </CardTitle>
              <CardDescription>
                Sitedeki metinleri deploy gerektirmeden düzenleyin. Tüm değişiklikler anında yayına alınır.
              </CardDescription>
            </div>
            <Button onClick={openNewDialog} className="gap-2" data-testid="add-translation-btn">
              <Plus className="w-4 h-4" />
              Yeni Çeviri Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Anahtar veya çeviri ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="translation-search"
                />
              </div>
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kategoriler</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.key} value={cat.key}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Nasıl Çalışır?</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Buradan eklediğiniz çeviriler veritabanında saklanır ve sitedeki varsayılan metinleri geçersiz kılar. 
                  Deploy yapmanıza gerek kalmadan değişiklikler anında görünür.
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{translations.length}</div>
              <div className="text-sm text-gray-500">Toplam Çeviri</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{SUPPORTED_LANGUAGES.length}</div>
              <div className="text-sm text-gray-500">Desteklenen Dil</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {translations.filter(t => getTranslationCompleteness(t) === 100).length}
              </div>
              <div className="text-sm text-gray-500">Tam Çeviri</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-amber-600">
                {translations.filter(t => getTranslationCompleteness(t) < 100).length}
              </div>
              <div className="text-sm text-gray-500">Eksik Çeviri</div>
            </div>
          </div>

          {/* Translations Table */}
          {filteredTranslations.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">
                {searchQuery || categoryFilter !== 'all' ? 'Sonuç bulunamadı' : 'Henüz çeviri eklenmemiş'}
              </h3>
              <p className="text-gray-500 mt-1">
                {searchQuery || categoryFilter !== 'all' 
                  ? 'Farklı bir arama veya filtre deneyin'
                  : 'Yeni bir çeviri ekleyerek başlayın'}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Anahtar</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Türkçe Önizleme</TableHead>
                    <TableHead className="w-[100px]">Tamamlanma</TableHead>
                    <TableHead className="w-[100px] text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTranslations.map((translation) => (
                    <TableRow key={translation.id} data-testid={`translation-row-${translation.id}`}>
                      <TableCell className="font-mono text-sm">{translation.key}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {CATEGORIES.find(c => c.key === translation.category)?.name || translation.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate">
                        {translation.translations?.tr || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${getTranslationCompleteness(translation) === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ width: `${getTranslationCompleteness(translation)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{getTranslationCompleteness(translation)}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(translation)}
                            data-testid={`edit-translation-${translation.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, translation })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-translation-${translation.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, translation: null, isNew: false })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editDialog.isNew ? 'Yeni Çeviri Ekle' : 'Çeviriyi Düzenle'}
            </DialogTitle>
            <DialogDescription>
              Tüm dillerde çevirileri girin. Boş bırakılan alanlar varsayılan değerleri kullanır.
            </DialogDescription>
          </DialogHeader>
          
          {editDialog.translation && (
            <TranslationForm
              translation={editDialog.translation}
              isNew={editDialog.isNew}
              onSave={handleSave}
              onCancel={() => setEditDialog({ open: false, translation: null, isNew: false })}
              saving={saving}
              activeLanguage={activeLanguage}
              setActiveLanguage={setActiveLanguage}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, translation: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Çeviriyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteDialog.translation?.key}</strong> anahtarını silmek istediğinizden emin misiniz? 
              Bu işlem geri alınamaz ve tüm dillerdeki çeviriler silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </TabsContent>

        <TabsContent value="seller-status">
          <SellerTranslationStatusTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Translation Form Component
const TranslationForm = ({ translation, isNew, onSave, onCancel, saving, activeLanguage, setActiveLanguage }) => {
  const [formData, setFormData] = useState({
    key: translation.key || '',
    category: translation.category || 'custom',
    translations: translation.translations || {}
  });

  const handleTranslationChange = (langCode, value) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [langCode]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="key">Çeviri Anahtarı *</Label>
          <Input
            id="key"
            value={formData.key}
            onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value }))}
            placeholder="örn: welcome_message"
            disabled={!isNew}
            className="font-mono"
            required
            data-testid="translation-key-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Kodda kullanılacak benzersiz anahtar (değiştirilemez)
          </p>
        </div>
        <div>
          <Label htmlFor="category">Kategori</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
            <SelectTrigger id="category" data-testid="translation-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.key} value={cat.key}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Tabs value={activeLanguage} onValueChange={setActiveLanguage}>
          <div className="bg-gray-50 border-b px-4">
            <TabsList className="h-auto p-0 bg-transparent gap-0">
              {SUPPORTED_LANGUAGES.map(lang => (
                <TabsTrigger
                  key={lang.code}
                  value={lang.code}
                  className="px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:bg-white"
                >
                  <span className="mr-2">{lang.flag}</span>
                  {lang.name}
                  {formData.translations[lang.code]?.trim() && (
                    <Check className="w-3 h-3 ml-2 text-green-500" />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          
          {SUPPORTED_LANGUAGES.map(lang => (
            <TabsContent key={lang.code} value={lang.code} className="m-0 p-4">
              <div>
                <Label htmlFor={`trans-${lang.code}`}>{lang.flag} {lang.name} Çevirisi</Label>
                <Textarea
                  id={`trans-${lang.code}`}
                  value={formData.translations[lang.code] || ''}
                  onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
                  placeholder={`${lang.name} dilinde metin girin...`}
                  rows={4}
                  className="mt-2"
                  data-testid={`translation-${lang.code}-input`}
                />
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          İptal
        </Button>
        <Button type="submit" disabled={saving || !formData.key.trim()} data-testid="save-translation-btn">
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Kaydediliyor...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Kaydet
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default TranslationManagementTab;
